/* eslint-disable @next/next/no-img-element */
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BorderBeam } from 'border-beam';
import classNames from 'classnames';
import { useDrop } from 'ahooks';
import MediaInput from '$/components/MediaInput';
import InputPanelToolBar from '$/components/InputPanelToolBar';
import InputTextArea from '$/components/InputTextAreaV2';
import type { V3CanvasHomeCategoryInfo } from '$/domains/caseV3/type';
import AssetLibraryModalContent from '@/components/AssetModalContent/AssetLibraryModalContent';
import HistoryModalContent from '@/components/AssetModalContent/HistoryModalContent';
import type { MaterialTypeValue } from '@/components/AssetModalContent/type';
import { useCommonStore } from '@/store/useCommonStore';
import { UseInputComposerResult } from '$/hooks/useInputComposer/types';
import { DEFAULT_INPUT_COMPOSER_UPLOAD_LIMITS } from '$/hooks/useInputComposer';
import useCreateRoom from '$/hooks/useCreateRoom';
import type { InputPanelAssetTrackLocation, SkillTrackParams, UploadButtonItem } from '$/components/InputPanelToolBar/types';
import { openPreview } from '$/store/usePreviewStore';
import type { PreviewMediaItem } from '$/pages/RoboneoChatBoard/type';
import { mtstatTrack } from '$/utils/mtstat';
import { useLanguageStore } from '$/store/languageStore';
import styles from './index.module.scss';

const HOME_ASSET_LIBRARY_ALLOWED_TYPES: MaterialTypeValue[] = [
  'image', 'video', 'audio', 'document', 'excel', 'ppt',
];

type Props = {
  className?: string;
  inputPlaceholder?: string;
  inputComposer: UseInputComposerResult;
  selectedPromptGroup?: V3CanvasHomeCategoryInfo | null;
  trackLocation?: string;
  /** 创建房间埋点 location，默认与 trackLocation 一致 */
  createTrackLocation?: string;
  assetTrackLocation?: InputPanelAssetTrackLocation;
  renderSubmitButton?: (params: {
    disabled: boolean;
    onClick: () => void;
  }) => React.ReactNode;
};

/**
 * 渲染 BaseHomeNew 首屏新版输入框。
 * 交互逻辑：
 * 1. 上传资源展示在输入框上方预览区（composer.resourceList），不直接进入编辑器内容；
 *    用户可通过 @ 将已上传的资源插入到编辑器中引用。
 * 2. 删除预览区某个资源时，编辑器中对该资源的引用全部删除，重置操作栈。
 * 3. 发送时：文本内容取编辑器 serialized content，媒体资源取全部已上传列表。
 */
function HomeInputPanel({
  className,
  inputPlaceholder,
  inputComposer,
  selectedPromptGroup,
  trackLocation = 'home_page',
  createTrackLocation,
  assetTrackLocation = 'home_page',
  renderSubmitButton,
}: Props) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const initUserInfo = useCommonStore((state) => state.initUserInfo);
  const { loading, createRoom } = useCreateRoom({
    onLoginSuccessed: async () => {
      await initUserInfo();
    },
  });

  const [isInputPanelActive, setIsInputPanelActive] = useState(false);
  const [resourcePopupType, setResourcePopupType] = useState<'history' | 'assetLibrary'>('history');
  const inputPanelRef = useRef<HTMLDivElement>(null);
  const isSubmitDisabled = inputComposer.isSubmitDisabled || loading;
  const selectedSkillList = inputComposer.selectedSkillInfoList;
  const shouldShowMediaInput = inputComposer.resourceList.length > 0
    || inputComposer.uploading
    || selectedSkillList.length > 0;
  /** 首页上传菜单点击统一上报 upload_control_click。 */
  const trackUploadControlClick = useCallback((mediaType: string) => {
    mtstatTrack('upload_control_click', {
      location: trackLocation,
      media_type: mediaType,
      trigger_type: 'click',
      thinking_mode: inputComposer.agentThinkingModeType,
    });
  }, [inputComposer.agentThinkingModeType, trackLocation]);

  /** 首页保留历史记录入口，并追加同款资产库资源选择入口。 */
  const trackedUploadButtonList = useMemo<UploadButtonItem[]>(() => {
    const buttonList = inputComposer.uploadButtonList.map((item) => ({
      ...item,
      onClick: () => {
        if (item.key === 'asset') setResourcePopupType('history');
        trackUploadControlClick(item.key);
        item.onClick();
      },
    }));
    const historyButton = buttonList.find((item) => item.key === 'asset');
    return [
      ...buttonList,
      {
        key: 'assetLibrary',
        icon: 'folderOpenBold',
        text: languageStore.smartcatTranslationText((v) => v.upload_from_asset_library),
        disabled: historyButton?.disabled ?? true,
        onClick: () => {
          setResourcePopupType('assetLibrary');
          trackUploadControlClick('assetLibrary');
          mtstatTrack('roboneo_asset_upload_click_new', {
            location: 'home_page',
            room_id: '',
          });
          inputComposer.actions.openAssetLibrary();
        },
      },
    ];
  }, [
    inputComposer.actions,
    inputComposer.uploadButtonList,
    languageStore,
    trackUploadControlClick,
  ]);
  const assetLibraryAddLabel = languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_studio_tool_add);
  const remainingImageCount = Math.max(0, DEFAULT_INPUT_COMPOSER_UPLOAD_LIMITS.maxImageNum - inputComposer.images.length);
  const remainingVideoCount = Math.max(0, DEFAULT_INPUT_COMPOSER_UPLOAD_LIMITS.maxVideoNum - inputComposer.videos.length);
  const remainingDocumentCount = Math.max(0, DEFAULT_INPUT_COMPOSER_UPLOAD_LIMITS.maxDocumentNum - inputComposer.documents.length);
  const assetLibraryMaxSelectableCount = remainingImageCount + remainingVideoCount + remainingDocumentCount;

  /** 监听拖拽/粘贴上传，上传后的资源进入新版输入框预览区。 */
  useDrop(inputPanelRef, {
    onFiles(files: File[], event?: React.DragEvent) {
      inputComposer.actions.uploadDroppedFiles(files, event?.type);
    },
  });

  // ─── 输入框光效激活态 ──────────────────────────────────────────────────
  const handleInputFocus = useCallback(() => {
    setIsInputPanelActive(true);
  }, []);

  // ─── 输入框光效离开态 ──────────────────────────────────────────────────
  const handleInputBlur = useCallback(() => {
    setIsInputPanelActive(false);
  }, []);

  /** 点击预览区图片/视频时打开公共预览弹框，文件资源不在输入框内预览。 */
  const handlePreviewResource = useCallback((resource: UseInputComposerResult['resourceList'][number]) => {
    if (!resource.url || (resource.mediaType !== 'image' && resource.mediaType !== 'video')) return;
    const previewMediaList: PreviewMediaItem[] = inputComposer.resourceList
      .filter((item) => item.url && (item.mediaType === 'image' || item.mediaType === 'video'))
      .map((item) => ({
        url: item.url,
        cover: item.coverUrl,
        width: item.width,
        height: item.height,
        type: item.mediaType as 'image' | 'video',
        task_id: '',
        name: item.name,
        asset_id: item.asset_id ? `${item.asset_id}` : undefined,
      }));
    const initialIndex = previewMediaList.findIndex((item) => item.url === resource.url);
    if (initialIndex === -1) return;
    openPreview({
      previewImageList: previewMediaList,
      initialIndex,
      previewShowDownload: false,
    });
  }, [inputComposer.resourceList]);

  /** 上报 Skill 弹框内容曝光，按图中规范补齐 tab、skill 和位置参数。 */
  const trackSkillShow = useCallback((params: SkillTrackParams) => {
    if (!params.skill) return;
    mtstatTrack('roboneo_input_skills_show', {
      tab_id: `${params.tab.id}`,
      tab_name: params.tab.name,
      skill_id: `${params.skill.id}`,
      skill_name: params.skill.name,
      location: trackLocation,
    });
  }, [trackLocation]);

  /** 上报 Skill 弹框内容点击，选择和取消选择都按实际点击内容记录。 */
  const trackSkillClick = useCallback((params: SkillTrackParams) => {
    if (!params.skill) return;
    mtstatTrack('roboneo_input_skills_click', {
      tab_id: `${params.tab.id}`,
      tab_name: params.tab.name,
      skill_id: `${params.skill.id}`,
      skill_name: params.skill.name,
      location: trackLocation,
    });
  }, [trackLocation]);

  /** 上报 Skill 弹框分类 tab 点击，点击一次上报一次。 */
  const trackSkillTabClick = useCallback((params: SkillTrackParams) => {
    mtstatTrack('roboneo_input_skills_tab_click', {
      tab_id: `${params.tab.id}`,
      tab_name: params.tab.name,
      location: trackLocation,
    });
  }, [trackLocation]);

  // ─── 提交 ──────────────────────────────────────────────────────────────
  const submitPrompt = useCallback(() => {
    createRoom({
      prompt: inputComposer.inputText,
      markElementList: inputComposer.resourceList,
      agentInfoList: inputComposer.agentInfoList,
      usedModel: inputComposer.usedModel,
      activeMode: inputComposer.activeMode,
      agentThinkingModeType: inputComposer.agentThinkingModeType,
      trackLocation: createTrackLocation ?? trackLocation,
      isAutoSubmit: true,
      skillList: selectedSkillList.length ? selectedSkillList : undefined,
      autoPrefixUnreferencedResources: false,
    });
  }, [
    createTrackLocation,
    inputComposer.resourceList,
    inputComposer.agentInfoList,
    inputComposer.inputText,
    inputComposer.usedModel,
    inputComposer.activeMode,
    inputComposer.agentThinkingModeType,
    selectedSkillList,
    trackLocation,
    createRoom,
  ]);

  return (
    <BorderBeam
      size={isInputPanelActive ? 'line' : 'md'}
      colorVariant="ocean"
      duration={isInputPanelActive ? 2.4 : 1.96}
      strength={0.7}
    >
      <div
        ref={inputPanelRef}
        className={classNames(styles.homeInputPanel, className)}
        data-node-id="36984:127540"
      >
        {
        shouldShowMediaInput ? (
          <MediaInput
            className={classNames(styles.mediaInput)}
            previews={inputComposer.resourceList}
            onRemove={inputComposer.actions.removeResource}
            onItemClick={handlePreviewResource}
            skillList={selectedSkillList}
            onSkillRemove={inputComposer.actions.removeSelectedSkill}
            isLoading={inputComposer.uploading}
            loadingCount={inputComposer.uploadingCount}
          />
        ) : null
      }
        <div className={styles.inputTextBox}>
          <InputTextArea
            ref={inputComposer.actions.inputRef}
            className={styles.textarea}
            placeholder={inputPlaceholder}
            initialValue={inputComposer.editorContent}
            atImageOptions={inputComposer.atImageOptions}
            atImagePopupPlacement="top"
            atImagePopupEmptyText={languageStore.smartcatTranslationText((v) => v.no_asset_to_reference)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onBridgeReady={(bridge) => {
              if (inputComposer.editorContent.length) {
                bridge.setValue(inputComposer.editorContent);
              }
            }}
            maxLength={5000}
            onChange={(params) => { inputComposer.actions.setEditorContent(params.value); }}
            onEnter={() => { if (!isSubmitDisabled) submitPrompt(); }}
          />
        </div>

        <InputPanelToolBar
          uploadButtonList={trackedUploadButtonList}
          onUploadLimitToast={inputComposer.actions.showUploadLimitToast}
          uploadPopoverOpen={inputComposer.uploadPopoverOpen}
          onUploadPopoverOpenChange={inputComposer.actions.setUploadPopoverOpen}
        // showModelSelect={showModelSelect}
          modelMap={inputComposer.modelMap}
          usedModel={inputComposer.usedModel}
          setUsedModel={inputComposer.actions.setUsedModel}
          showSkillSelect={inputComposer.showSkillSelect}
          skillCategories={inputComposer.skillCategories}
          skillList={inputComposer.skillList}
          selectedSkill={inputComposer.selectedSkillInfo}
          onSkillSelect={inputComposer.actions.setSelectedSkill}
          onSkillShow={trackSkillShow}
          onSkillClick={trackSkillClick}
          onSkillTabClick={trackSkillTabClick}
          isSubmitDisabled={isSubmitDisabled}
          onSubmit={submitPrompt}
          assetLibraryVisible={inputComposer.assetLibraryVisible}
          onCloseAssetLibrary={inputComposer.actions.closeAssetLibrary}
          renderAssetModalContent={(props) => {
            if (resourcePopupType === 'assetLibrary') {
              return React.createElement(
                'div',
                { className: styles.assetLibraryDialog },
                React.createElement(AssetLibraryModalContent, {
                  ...props,
                  insertTarget: 'home_page',
                  pickerSource: 'subjectLibrary',
                  maxSelectableCount: assetLibraryMaxSelectableCount,
                  batchConfirmLabel: assetLibraryAddLabel,
                  assetActionLabel: assetLibraryAddLabel,
                  enableAddCanvasClickTrack: false,
                  allowedMaterialTypes: HOME_ASSET_LIBRARY_ALLOWED_TYPES,
                }),
              );
            }
            return React.createElement(HistoryModalContent, props);
          }}
          onAssetItemClick={(item) => { inputComposer.actions.addAssetResources([item]); }}
          onAssetBatchConfirm={(items) => { inputComposer.actions.addAssetResources(items); }}
          trackLocation={assetTrackLocation}
          renderSubmitButton={renderSubmitButton
            ? () => renderSubmitButton({
              disabled: isSubmitDisabled,
              onClick: submitPrompt,
            })
            : undefined}
        />
      </div>
    </BorderBeam>

  );
}

export default React.memo(HomeInputPanel);
